import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Calendar, Users, Globe, Clock } from "lucide-react";
import { api, userApi, money, formatApiErrorDetail } from "../lib/api";
import { track } from "../lib/analytics";
import { useUser } from "../context/UserAuthContext";
import Seo from "../components/Seo";
import { toast } from "sonner";

const inp = "w-full px-4 py-3 text-sm rounded-sm border border-brand-gold-light/50 bg-white outline-none focus:border-brand-gold";

export default function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useUser();
  const [e, setE] = useState(null);
  const [nf, setNf] = useState(false);
  const [form, setForm] = useState({ participant_name: "", participant_email: "", participant_phone: "", participant_age: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // {status, position?}

  const load = () => api.get(`/events/${slug}`).then(({ data }) => { setE(data); track("event_view", { event: data.title }); }).catch(() => setNf(true));
  useEffect(() => { setE(null); setNf(false); setDone(null); load(); }, [slug]);
  useEffect(() => { if (user) setForm((f) => ({ ...f, participant_name: user.name || "", participant_email: user.email || "", participant_phone: user.phone || "" })); }, [user]);

  if (nf) return <div className="pt-40 pb-32 text-center"><h1 className="font-serif text-4xl text-brand-green">Etkinlik bulunamadı</h1><Link to="/etkinlikler" className="text-brand-gold mt-4 inline-block">Etkinlikler</Link></div>;
  if (!e) return <div className="pt-40 pb-40 text-center text-brand-text/50">Yükleniyor…</div>;

  const unlimited = e.remaining == null;
  const full = !unlimited && e.remaining <= 0;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doRegister = async (waitlist) => {
    if (!user) { nav(`/giris?next=/etkinlikler/${slug}`); return; }
    setLoading(true);
    track("booking_started", { event: e.title });
    try {
      const path = waitlist ? `/events/${slug}/waitlist` : `/events/${slug}/register`;
      const payload = { ...form, participant_age: form.participant_age ? parseInt(form.participant_age) : null };
      const { data } = await userApi.post(path, payload);
      setDone(data);
      if (data.status === "confirmed") track("booking_completed", { event: e.title });
      if (data.status === "waitlisted") track("waitlist_joined", { event: e.title });
      load();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409 && msg.includes("dolu")) toast.error("Kontenjan doldu. Bekleme listesine katılabilirsiniz.");
      else toast.error(msg);
    } finally { setLoading(false); }
  };

  const jsonLd = { "@context": "https://schema.org", "@type": "Event", name: e.title, startDate: e.start, description: e.description,
    eventAttendanceMode: e.is_online ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
    location: e.is_online ? { "@type": "VirtualLocation", url: "https://bilgece.life" } : { "@type": "Place", name: e.location, address: e.address } };

  return (
    <>
      <Seo title={e.title} description={e.description} image={e.cover} type="article" jsonLd={jsonLd} />
      <section className="relative pt-36 pb-20 grain overflow-hidden">
        <div className="absolute inset-0"><img src={e.cover} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-brand-green/85" /></div>
        <div className="relative container-narrow px-6 lg:px-10 text-brand-offwhite">
          <Link to="/etkinlikler" className="text-brand-offwhite/70 text-sm hover:text-brand-offwhite">← Etkinlikler</Link>
          <p className="text-xs uppercase tracking-[0.25em] text-brand-gold-light font-semibold mt-4">{e.category}</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light max-w-3xl mt-2">{e.title}</h1>
        </div>
      </section>

      <section className="section-pad bg-brand-offwhite">
        <div className="container-narrow grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="text-brand-text/75 text-lg leading-relaxed">{e.description}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Info icon={Calendar} label="Tarih & Saat" value={(e.start || e.date || "").replace("T", " ")} />
              <Info icon={e.is_online ? Globe : MapPin} label="Konum" value={`${e.location}${e.address ? " — " + e.address : ""}`} />
              <Info icon={Users} label="Kontenjan" value={full ? "Dolu" : unlimited ? "Sınırsız kontenjan" : `${e.remaining} kişilik yer kaldı`} />
              <Info icon={Clock} label="Eğitmen" value={e.instructor || "Bilgece Eğitmeni"} />
              {(e.min_age || e.max_age) && <Info icon={Users} label="Yaş" value={`${e.min_age || "?"}–${e.max_age || "?"}`} />}
              <Info icon={Clock} label="Ücret" value={e.price > 0 ? money(e.price) : "Ücretsiz"} />
            </div>
          </div>

          <div className="bg-brand-cream border border-brand-gold-light/40 rounded-sm p-7 h-fit">
            {done ? (
              <div data-testid="booking-result" className="text-center py-6">
                <div className="font-serif text-2xl text-brand-green">{done.status === "confirmed" ? "Kaydın onaylandı!" : done.status === "waitlisted" ? "Bekleme listesindesin" : "Kaydın alındı"}</div>
                <p className="text-brand-text/70 text-sm mt-2">{done.status === "waitlisted" ? `Sıra numaran: ${done.position}. Yer açılırsa seninle iletişime geçeceğiz.` : "Etkinlik detaylarını e-posta ile paylaşacağız."}</p>
                <Link to="/hesabim/etkinliklerim" className="inline-block mt-5 bg-brand-gold text-brand-offwhite px-6 py-3 rounded-sm font-semibold">Etkinliklerim</Link>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-brand-green mb-1">{full ? "Kontenjan dolu" : "Yerini ayırt"}</h3>
                <p className="text-brand-text/60 text-sm mb-5">{full ? "Bekleme listesine katılabilirsin." : e.price > 0 ? money(e.price) : "Ücretsiz katılım"}</p>
                <div className="space-y-3">
                  <input placeholder="Katılımcı adı" value={form.participant_name} onChange={(ev) => set("participant_name", ev.target.value)} className={inp} data-testid="booking-name" />
                  <input type="email" placeholder="E-posta" value={form.participant_email} onChange={(ev) => set("participant_email", ev.target.value)} className={inp} data-testid="booking-email" />
                  <input placeholder="Telefon" value={form.participant_phone} onChange={(ev) => set("participant_phone", ev.target.value)} className={inp} data-testid="booking-phone" />
                  {(e.min_age || e.max_age) && <input placeholder="Yaş" value={form.participant_age} onChange={(ev) => set("participant_age", ev.target.value)} className={inp} />}
                  {full ? (
                    <button onClick={() => doRegister(true)} disabled={loading} data-testid="waitlist-btn" className="w-full bg-brand-green text-brand-offwhite py-3 rounded-sm font-semibold disabled:opacity-60">Bekleme Listesine Katıl</button>
                  ) : (
                    <button onClick={() => doRegister(false)} disabled={loading} data-testid="register-btn" className="w-full bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors py-3 rounded-sm font-semibold disabled:opacity-60">{loading ? "İşleniyor..." : user ? "Kaydımı Oluştur" : "Giriş yap ve kaydol"}</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Info({ icon: Icon, label, value }) {
  return <div className="flex gap-3"><Icon className="text-brand-gold shrink-0 mt-1" size={18} /><div><p className="text-xs uppercase tracking-wide text-brand-text/50">{label}</p><p className="text-brand-text/85 text-sm">{value}</p></div></div>;
}
